import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { alpha } from "@mui/material/styles";
import { MODE_META, type ConversationMode } from "./modeMeta";

const MODES: ConversationMode[] = ["pre_call", "post_call"];

interface ConversationModeCardsProps {
  value: ConversationMode;
  onChange: (mode: ConversationMode) => void;
}

/**
 * Selectable two-card chooser for the AI Coach tools (Pre-Call Research vs
 * Call Analysis). Each card carries its own icon, color accent, and description
 * so the two workflows are visually distinct. Selection is conveyed by a colored
 * border, tinted background, AND a check icon (not color alone).
 */
export function ConversationModeCards({ value, onChange }: ConversationModeCardsProps) {
  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {MODES.map((mode) => {
        const meta = MODE_META[mode];
        const selected = value === mode;
        const { Icon } = meta;
        return (
          <Grid key={mode} size={{ xs: 12, sm: 6 }}>
            <Card
              variant="outlined"
              sx={{
                height: "100%",
                borderWidth: 2,
                borderColor: selected ? `${meta.color}.main` : "divider",
                bgcolor: (theme) =>
                  selected ? alpha(theme.palette[meta.color].main, 0.08) : "transparent",
              }}
            >
              <CardActionArea
                onClick={() => onChange(mode)}
                aria-pressed={selected}
                sx={{ p: 2, height: "100%" }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <Icon color={meta.color} />
                  <Typography variant="subtitle1" sx={{
                    fontWeight: 700
                  }}>
                    {meta.label}
                  </Typography>
                  {selected && (
                    <CheckCircleIcon
                      color={meta.color}
                      fontSize="small"
                      sx={{ ml: "auto" }}
                    />
                  )}
                </Box>
                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  {meta.description}
                </Typography>
              </CardActionArea>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
}
